import { type Page, success, type UserInfo } from "@iflyrpa/share";
import type { Response } from "playwright-core";
import type { SteelAction } from "./index";

/** 视频号助手 finder 账号信息 */
interface FinderInfo {
    finderUsername: string;
    nickname: string;
    headImgUrl: string;
    uniqId: string;
    isMasterFinder: boolean;
}

/**
 * 视频号登录 RPA
 *
 * 流程：
 * 1. 打开视频号助手登录页，生成远程调试地址
 * 2. 拦截不必要资源（mp4/ttf），提升加载速度
 * 3. 监听 auth_login_status 接口判断扫码状态
 * 4. 扫码确认后切换监听 auth_data 获取用户信息和 finder 列表
 * 5. 提取 localStorage 中的 fingerPrintDeviceId 和 aId
 * 6. 收集 cookie 并返回结果
 */
export const rpaAction: SteelAction = async (task, _params) => {
    const reporter = task.reportService?.reportLoginStatus ?? (() => Promise.resolve());

    let page: Page;
    /** 标记扫码状态：new → scanned → confirmed */
    let statusMachine: "new" | "scanned" | "confirmed" = "new";
    let isSelfClosing = false;
    let handleBrowserDisconnected: (() => void) | undefined;
    const cleanup = async () => {
        if (handleBrowserDisconnected) {
            task._steelBrowser?.off("disconnected", handleBrowserDisconnected);
        }
        if (page && !page.isClosed()) {
            await page.close().catch(() => {});
        }
    };

    // ========== 第一步：启动浏览器并打开登录页 ==========
    try {
        page = await task.createPage({
            url: "https://channels.weixin.qq.com/login.html",
        });

        if (!task._session?.id) throw new Error("Session ID 不存在");

        handleBrowserDisconnected = () => {
            if (task.debug && isSelfClosing) {
                task.logger.info("执行结束，关闭浏览器！");
            } else {
                task.logger.info("浏览器被手动释放或意外关闭！");
            }
        };
        task._steelBrowser?.on("disconnected", handleBrowserDisconnected);
    } catch (e) {
        task.logger.error("浏览器启动失败", e);
        return { code: 414, message: `浏览器启动失败: ${e}`, data: {} };
    }

    // 拦截不必要的资源，提升加载速度
    await page.route("**/*.mp4", (route) => route.abort());
    await page.route("**/*.ttf", (route) => route.abort());
    await page.addInitScript(() => {
        window.requestAnimationFrame = () => 0;
    });

    task.logger.info("页面创建成功，等待二维码加载...");

    // 等待页面稳定
    await page.waitForTimeout(2000);

    // ========== 第二步：生成远程调试地址 ==========
    const pageInfo = await task._client?.sessions.liveDetails(task._session.id);
    const pageId = pageInfo?.pages[0].id ?? "";

    const baseHost = task._session.websocketUrl?.replace(/^wss?:\/\//, "") ?? "";
    const connectAddress = new URLSearchParams({ baseHost, pageId }).toString();

    task.reportService?.settingSessionID?.(task._session?.id || "");

    task.logger.info("远程调试地址已生成，现在可以安全访问远程页面");
    await reporter(0, "浏览器启动成功，请使用微信App扫码登录……", { connectAddress });

    // ========== 第三步：监听扫码状态与登录结果 ==========
    const userInfo: UserInfo = { finderUsername: "", uniqueId: "", avatar: "", name: "" };
    let finderList: FinderInfo[] = [];

    try {
        await new Promise<void>((resolve, reject) => {
            let finished = false;

            /** 清理所有监听器，防止重复触发 */
            const cleanup = (status?: unknown) => {
                if (finished) return;
                finished = true;
                page.off("response", handleAuthData);
                page.off("response", handleAuthLoginStatus);
                if (status !== undefined) reject(status);
            };

            /**
             * 登录成功后获取用户数据
             *
             * 监听两个接口：
             * - auth_data：获取 finder 用户信息和多账号列表
             * - helper_upload_params：获取用户 uin（微信号标识）
             */
            const handleAuthData = async (resp: Response) => {
                try {
                    const url = resp.url();

                    if (
                        url.includes("/cgi-bin/mmfinderassistant-bin/auth/auth_data") ||
                        url.includes("/mmfinderassistant-bin/auth/auth_data")
                    ) {
                        await reporter(0, "扫码成功!", { stage: "scanQrcode", connectAddress });
                        try {
                            const jsonResponse = await resp.json();

                            if (jsonResponse.errCode === 0 && jsonResponse.data) {
                                const data = jsonResponse.data;

                                // 获取主视频号信息
                                if (data.finderUser) {
                                    userInfo.name = data.finderUser.nickname || "";
                                    userInfo.uniqueId = data.finderUser.uniqId || "";
                                    userInfo.avatar = data.finderUser.headImgUrl || "";
                                    userInfo.finderUsername = data.finderUser.finderUsername || "";
                                }

                                // 视频号助手支持多账号管理，获取完整列表
                                if (data.finderList && Array.isArray(data.finderList)) {
                                    finderList = data.finderList.map((f: any) => ({
                                        finderUsername: f.finderUsername,
                                        nickname: f.nickname,
                                        headImgUrl: f.headImgUrl,
                                        uniqId: f.uniqId,
                                        isMasterFinder: f.isMasterFinder,
                                    }));
                                }

                                task.logger.info("auth_data 解析成功", {
                                    nickname: userInfo.name,
                                    finderCount: finderList.length,
                                });
                            }
                        } catch (e) {
                            task.logger.warn("解析 auth_data 失败:", {
                                error: e instanceof Error ? e.message : String(e),
                                url,
                            });
                        }
                    } else if (url.includes("/mmfinderassistant-bin/helper/helper_upload_params")) {
                        // 通过 upload_params 接口获取用户 uin
                        task.logger.info("检测到 helper_upload_params 接口");
                        cleanup();
                        try {
                            const jsonUserInfo: { data: { uin: string } } = await resp.json();
                            userInfo.uin = jsonUserInfo.data.uin ?? "";
                            task.logger.info("用户uin获取成功", { uin: userInfo.uin });
                        } catch (e) {
                            task.logger.warn("无法解析用户信息:", {
                                url,
                                error: e instanceof Error ? e.message : String(e),
                            });
                        }
                        resolve();
                    }
                } catch (err) {
                    task.logger.error("监听登录结果出现异常", err);
                    cleanup(err);
                }
            };

            /**
             * 监听视频号助手扫码状态接口 auth_login_status
             *
             * 状态码说明：
             *   status=0, acctStatus=0 → 未扫码
             *   status=5, acctStatus=1 → 已扫码，等待手机确认
             *   status=1, acctStatus=1 → 已确认登录
             *   status=3               → 登录成功（另一种状态）
             */
            const handleAuthLoginStatus = async (response: Response) => {
                try {
                    const url = response.url();

                    if (
                        !url.includes("/cgi-bin/mmfinderassistant-bin/auth/auth_login_status") &&
                        !url.includes("/mmfinderassistant-bin/auth/auth_login_status")
                    ) {
                        return;
                    }

                    let jsonResponse: {
                        errCode: number;
                        errMsg?: string;
                        data?: { status: number; acctStatus: number };
                    };

                    try {
                        jsonResponse = await response.json();
                    } catch (e) {
                        task.logger.warn("Response body unavailable", { url, error: e });
                        return;
                    }

                    if (jsonResponse.errCode !== 0) {
                        task.logger.warn("auth_login_status 返回错误:", {
                            errCode: jsonResponse.errCode,
                            errMsg: jsonResponse.errMsg,
                        });
                        return;
                    }

                    const data = jsonResponse.data;
                    if (!data) return;

                    const { status, acctStatus } = data;
                    task.logger.info("auth_login_status 状态:", { status, acctStatus });

                    // 未扫码，继续等待
                    if (status === 0 && acctStatus === 0) return;

                    // 已扫码，等待确认（仅首次上报）
                    if (status === 5 && acctStatus === 1) {
                        if (statusMachine !== "scanned") {
                            statusMachine = "scanned";
                            task.logger.info("用户已扫码，等待确认...");
                        }
                        return;
                    }

                    // 登录成功：切换监听 → auth_data
                    if ((status === 1 && acctStatus === 1) || status === 3) {
                        statusMachine = "confirmed";
                        task.logger.info("扫码确认，开始监听登录完成...");
                        page.off("response", handleAuthLoginStatus);
                        page.on("response", handleAuthData);
                    }
                } catch (err) {
                    task.logger.error("handleAuthLoginStatus 异常:", err);
                    cleanup(err);
                }
            };

            // 提前注册监听，防止丢失请求
            page.on("response", handleAuthLoginStatus);
        });
    } catch (e) {
        isSelfClosing = true;
        const errorMessage = e instanceof Error ? e.message : String(e);
        await cleanup();
        return { code: 500, message: errorMessage, data: { stage: "scanQrcode", connectAddress } };
    }

    // ========== 第四步：提取 localStorage 数据 ==========
    let localStorageData: Record<string, string> = {};
    const maxRetries = 10;

    task.logger.info("开始提取 localStorage 数据...");

    for (let retryCount = 0; retryCount < maxRetries; retryCount++) {
        try {
            localStorageData = await page.evaluate(() => ({
                fingerPrintDeviceId: localStorage.getItem("_finger_print_device_id") || "",
                aId: localStorage.getItem("__rx::aid") || "",
            }));

            if (localStorageData["fingerPrintDeviceId"] && localStorageData["aId"]) {
                task.logger.info("视频号 fingerPrintDeviceId & aId 数据已获取");
                break;
            }

            task.logger.info(`等待 localStorage 数据... (${retryCount + 1}/${maxRetries})`);
            await page.waitForTimeout(1000);
        } catch (error) {
            task.logger.warn(`提取 localStorage 数据失败 (尝试 ${retryCount + 1}/${maxRetries}):`, {
                error: error instanceof Error ? error.message : String(error),
            });
            await page.waitForTimeout(1000);
        }
    }

    // ========== 第五步：收集 cookie 并返回结果 ==========
    const cookie = JSON.stringify(await task._steelBrowserContext?.cookies());
    isSelfClosing = true;
    await cleanup();

    return success(
        {
            connectAddress,
            cookie,
            userInfo,
            finderList,
            extraParam: { ...localStorageData, uin: userInfo.uin, finderUserName: userInfo.finderUsername },
        },
        "登录成功！",
    );
};
